import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  MAESTRIA_CONTENT,
  getMaestriaContent,
  type MaestriaContent,
} from "@/content/maestria.content";

export type Locale = "pt" | "en";

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (nextLocale: Locale) => void;
  content: MaestriaContent;
}

export const LocaleContext = createContext<LocaleContextValue>({
  locale: "pt",
  setLocale: () => {},
  content: MAESTRIA_CONTENT,
});

export function resolveLocaleFromPath(pathname: string): Locale {
  const clean = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  if (clean === "/en" || clean.startsWith("/en/")) {
    return "en";
  }
  return "pt";
}

export function getInitialClientLocale(): Locale {
  if (typeof window === "undefined") return "pt";
  const path = window.location.pathname;
  const clean = path.split("?")[0].replace(/\/+$/, "") || "/";
  if (clean === "/en" || clean.startsWith("/en/")) return "en";
  if (clean === "/pt" || clean.startsWith("/pt/")) return "pt";
  if (document.documentElement.lang === "en") return "en";
  return "pt";
}

export function persistLocaleCookie(locale: Locale) {
  if (typeof document === "undefined") return;
  document.cookie = `au_locale=${locale}; path=/; max-age=31536000; samesite=lax`;
}

export function updateDocumentLang(locale: Locale) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale === "en" ? "en" : "pt-BR";
}

export function LocaleProvider({
  children,
  initialLocale,
}: Readonly<{
  children: ReactNode;
  initialLocale?: Locale;
}>) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? getInitialClientLocale());

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    persistLocaleCookie(nextLocale);
    updateDocumentLang(nextLocale);
    if (typeof window !== "undefined") {
      const nextPath = nextLocale === "en" ? "/en" : "/pt/";
      window.location.assign(nextPath + window.location.search + window.location.hash);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const current = getInitialClientLocale();
      setLocaleState(current);
      updateDocumentLang(current);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const content = getMaestriaContent(locale);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, content }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

export function useMaestriaContent(): MaestriaContent {
  const { content } = useLocale();
  return content;
}
